// @flow

import type { CollectionView } from 'wijmo/wijmo';

import type { GridItem } from '../types';
import { convertCasesAndBottlesToQty } from './convertCasesAndBottlesToQty';

const addOrUpdateItemFromCollection = ({
  changes,
  collection,
  Code,
  currentItem,
  precedence = 'server',
}: {
  changes: Array<{
    fieldName: string,
    updater?: [(item: GridItem) => mixed] | ((item: GridItem) => mixed),
    value: string,
  }>,
  collection: CollectionView,
  Code: string,
  currentItem: GridItem,
  precedence?: 'server' | 'local',
}) => {
  let item;

  if (!collection.items.some(i => i.Code === Code)) {
    item = collection.addNew();

    // this will take care of initializing all the available keys and
    // the updaters will take care of applying the neccessary logic
    // when needed.
    Object.keys(currentItem).forEach(key => {
      item[key] = currentItem[key];
    });

    changes.forEach(change => {
      if (change.updater) {
        if (typeof change.updater === 'function') {
          change.updater(item);
        } else if (Array.isArray(change.updater)) {
          change.updater.forEach(fn => {
            fn(item);
          });
        }
      } else {
        if (currentItem[change.fieldName] && precedence === 'server') {
          item[change.fieldName] = currentItem[change.fieldName];
        } else {
          item[change.fieldName] = change.value;
        }
      }
    });

    const Qty = convertCasesAndBottlesToQty(
      item.Cases,
      item.Bottles,
      currentItem.Uom,
    );

    item.Qty = Qty;

    if (currentItem.Mobo) {
      item.Mobo = currentItem.Mobo;
    }

    if (currentItem.MoboQty) {
      item.MoboQty = currentItem.MoboQty;
    }

    collection.commitNew();
  } else {
    item = collection.items.find(i => i.Code === Code);

    /**
     * if we were able to find the item it means we are dealing with an
     * `edit` of an existent item
     */
    if (item && Object.keys(item).length > 0) {
      collection.editItem(item);
      /**
       * NOTE: maxItemQty is updated by one of the `updaters` functions so
       * we don't have to manually update its values
       */
      item.maxItemQty = currentItem.maxItemQty;
      changes.forEach(change => {
        if (change.updater) {
          if (typeof change.updater === 'function') {
            change.updater(item);
          } else if (Array.isArray(change.updater)) {
            change.updater.forEach(fn => {
              fn(item);
            });
          }
        } else {
          /**
           * if `change.fieldName` exists it means is data coming from the Backend
           * so it will take precedence over local changes.
           */
          if (currentItem[change.fieldName] && precedence === 'server') {
            item[change.fieldName] = currentItem[change.fieldName];
          } else {
            item[change.fieldName] = change.value;
          }
        }
      });

      const Qty = convertCasesAndBottlesToQty(
        item.Cases,
        item.Bottles,
        currentItem.Uom,
      );

      item.Qty = Qty;

      if (currentItem.Mobo) {
        item.Mobo = currentItem.Mobo;
      }

      if (currentItem.MoboQty) {
        item.MoboQty = currentItem.MoboQty;
      }

      collection.commitEdit();
    }
  }

  return item;
};

export { addOrUpdateItemFromCollection };
